import type { ReactNode } from 'react';
import { Badge, Card, CardContent } from '@/ui/primitives';
import { MapPin } from 'lucide-react';
import { BandLadder } from '@/components/BandLadder';
import { CoverageTag } from '@/components/CoverageTag';
import { formatStationAddress } from '@/domain/address';
import type { BandId } from '@/domain/bands';
import { formatDistanceKm } from '@/domain/geo';
import type { Dictionary, Locale } from '@/i18n';
import type { LookupResult } from '@/hooks/useLookup';

type ResultPanelProps = {
  dict: Dictionary;
  locale: Locale;
  result: LookupResult;
  /* What the lead can do about this answer, rendered under the band message. It belongs
     inside the answer rather than in a card below it: the offer only makes sense as a
     reply to the sentence the lead has just read. */
  action?: ReactNode;
  /* `compact` is the embed. Same blocks in the same order — the answer does not change
     with the surface — drawn tighter and without a card of their own, because the panel
     they sit in is already the card and the section has one screen of height to spend. */
  density?: 'comfortable' | 'compact';
};

type BadgeVariant = 'green' | 'blue' | 'yellow' | 'red' | 'grey';

const BADGE = {
  excellent: 'green',
  covered: 'blue',
  limited: 'yellow',
  far: 'red',
  out_of_region: 'grey',
} satisfies Record<BandId, BadgeVariant>;

export const ResultPanel = ({
  dict,
  locale,
  result,
  action,
  density = 'comfortable',
}: ResultPanelProps) => {
  const { band, nearest, reach } = result;
  const distance = formatDistanceKm(nearest.distanceKm, locale);
  const compact = density === 'compact';

  const body = (
    <>
      {/* Out of region sits off the scale entirely, so four grey segments would be
          noise on the one message that has to land cleanly. */}
      {band === 'out_of_region' ? null : <BandLadder dict={dict} band={band} reach={reach} />}

      <div className="flex flex-col gap-2">
        <h2
          className={
            compact
              ? 'text-[17px] font-extrabold leading-tight tracking-tight text-black'
              : 'text-xl font-extrabold leading-tight tracking-tight text-black'
          }
        >
          {dict.bands[band].title}
        </h2>
        <p
          className={
            compact
              ? 'text-[13px] leading-snug text-grey-800'
              : 'text-sm leading-relaxed text-grey-800'
          }
        >
          {dict.bands[band].body}
        </p>
        {action ? <div className={compact ? 'pt-1' : 'pt-2'}>{action}</div> : null}
      </div>

      {band === 'out_of_region' ? null : (
        <div
          className={
            compact
              ? 'flex flex-col gap-1.5 border-t border-grey-200 pt-3'
              : 'flex flex-col gap-2 border-t border-grey-200 pt-4'
          }
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-grey-800">
            {dict.result.nearestStation}
          </span>

          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-grey-800" aria-hidden="true" />
            <span className={compact ? 'text-[13px] font-medium text-black' : 'text-sm font-medium text-black'}>
              {formatStationAddress(nearest.address)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-grey-800">{dict.result.supportedModels}</span>
            <CoverageTag models={nearest.bikeModels} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={BADGE[band]} className="tabular-nums">
              {distance}
            </Badge>
            <Badge variant="grey">
              {dict.result.stationsWithin(result.withinCount, result.withinRadiusKm)}
            </Badge>
          </div>

          <p className="text-[11px] text-grey-800">{dict.result.distanceNote}</p>
        </div>
      )}
    </>
  );

  if (compact) {
    return (
      <div role="status" aria-live="polite" className="animate-result-in flex flex-col gap-3.5">
        {body}
      </div>
    );
  }

  return (
    <Card role="status" aria-live="polite" className="animate-result-in border-grey-200">
      <CardContent className="flex flex-col gap-5 p-5">{body}</CardContent>
    </Card>
  );
};
